if (!customElements.get('product-slider-thumbnails')) {
  /**
   *  @class
   *  @function ProductSliderThumbnails
   */
  class ProductSliderThumbnails extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('change', this.setupProductGallery);
      this.onPdpSwapSuccess = this.onPdpSwapSuccess.bind(this);
      this.onLayoutResize = this.onLayoutResize.bind(this);
      this.stabilizeTimeout = null;
      this.resizeRaf = null;
    }
    connectedCallback() {
      this.product_container = this.closest('.thb-product-detail');
      this.thumbnail_container = this.product_container.querySelector('.product-thumbnail-container');
      this.video_containers = this.querySelectorAll('.product-single__media-external-video--play');
      this.mobileTwoImage = this.dataset.mobileTwoImage === 'true';
      this.pagination = this.product_container.querySelector('.product-images-buttons');
      this.progressBar = this.product_container.querySelector('.product-images-progress .flickity-progress--bar');
      this.initDone = false;

      this.setOptions();
      // Start Slider
      this.init();

      document.addEventListener('pdp:swap:success', this.onPdpSwapSuccess);
      if (typeof ResizeObserver === 'function') {
        this.layoutResizeObserver = new ResizeObserver(this.onLayoutResize);
        this.layoutResizeObserver.observe(this);
      }
    }
    disconnectedCallback() {
      this.flkty?.destroy();
      document.removeEventListener('pdp:swap:success', this.onPdpSwapSuccess);
      if (this.layoutResizeObserver) {
        this.layoutResizeObserver.disconnect();
        this.layoutResizeObserver = null;
      }
      if (this.resizeRaf) {
        cancelAnimationFrame(this.resizeRaf);
        this.resizeRaf = null;
      }
      if (this.stabilizeTimeout) {
        clearTimeout(this.stabilizeTimeout);
        this.stabilizeTimeout = null;
      }
    }
    setOptions() {
      this.hide_variants = this.dataset.hideVariants == 'true';
      this.thumbnails = this.thumbnail_container.querySelectorAll('.product-thumbnail');
      this.prev_button = this.querySelector('.flickity-prev');
      this.next_button = this.querySelector('.flickity-next');
      this.itemsPerPage = this.getItemsPerPage();
      const isTwoUpMobile = this.itemsPerPage > 1;
      this.options = {
        wrapAround: !isTwoUpMobile,
        pageDots: false,
        contain: true,
        adaptiveHeight: true,
        imagesLoaded: true,
        initialIndex: '.is-initial-selected',
        prevNextButtons: false,
        fade: false,
        cellSelector: '.product-images__slide.is-active',
        cellAlign: isTwoUpMobile ? 'left' : 'center',
        groupCells: isTwoUpMobile ? this.itemsPerPage : false
      };
    }
    init() {
      if (this.initDone) return;
      this.initDone = true;
      this.flkty = new Flickity(this, this.options);

      this.selectedIndex = this.flkty.selectedIndex;

      // Setup Events
      this.setupEvents();

      // Start Gallery
      this.setupProductGallery();
      this.playActiveVideos();
      this.bindImagesLoadedFix();
      this.forceViewportHeight();
      this.scheduleLayoutStabilization();
    }
    reInit(imageSetIndex) {

      this.flkty.destroy();

      this.setOptions();

      this.flkty = new Flickity(this, this.options);

      // Setup Events
      this.setupEvents();

      this.selectedIndex = this.flkty.selectedIndex;
      this.bindImagesLoadedFix();
      this.playActiveVideos();
      this.forceViewportHeight();
      this.scheduleLayoutStabilization();
    }
    setupEvents() {
      const _this = this;
      if (this.prev_button) {
        let prev = this.prev_button.cloneNode(true);
        this.prev_button.parentNode.append(prev);
        this.prev_button.remove();
        prev.addEventListener('click', (event) => {
          this.flkty.previous();
        });
        prev.addEventListener('keyup', (event) => {
          this.flkty.previous();
          event.preventDefault();
        });
      }
      if (this.next_button) {
        let next = this.next_button.cloneNode(true);
        this.next_button.parentNode.append(next);
        this.next_button.remove();
        next.addEventListener('click', (event) => {
          this.flkty.next();
        });
        next.addEventListener('keyup', (event) => {
          this.flkty.next();
          event.preventDefault();
        });
      }

      this.bindPaginationButtons();
      this.video_containers.forEach((container) => {
        container.querySelector('button').addEventListener('click', () => {
          const externalVideo = container.closest('.product-single__media-external-video');
          if (!externalVideo) return;

          if (externalVideo.dataset.videoLoaded !== 'true') {
            const template = externalVideo.querySelector('.product-single__media-external-video-template');
            if (template) {
              externalVideo.appendChild(template.content.cloneNode(true));
              externalVideo.dataset.videoLoaded = 'true';
            }
          }

          container.setAttribute('hidden', '');
          this.playExternalVideo(externalVideo);
        });
      });
      this.flkty.on('settle', function (index) {
        _this.selectedIndex = index;
        _this.updatePagination();
      });
      this.flkty.on('change', (index) => {

        let previous_slide = this.flkty.cells[_this.selectedIndex].element,
          previous_media = previous_slide.querySelector('.product-single__media'),
          active_thumbs = Array.from(this.thumbnails).filter(element => element.classList.contains('is-active')),
          active_thumb = active_thumbs[index] ? active_thumbs[index] : active_thumbs[0];

        this.thumbnails.forEach((item, i) => {
          item.classList.remove('is-initial-selected');
        });
        active_thumb.classList.add('is-initial-selected');

        requestAnimationFrame(() => {
          if (active_thumb.offsetParent === null) {
            return;
          }
          const windowHalfHeight = active_thumb.offsetParent.clientHeight / 2,
            windowHalfWidth = active_thumb.offsetParent.clientWidth / 2;
          active_thumb.parentElement.scrollTo({
            left: active_thumb.offsetLeft - windowHalfWidth + active_thumb.clientWidth / 2,
            top: active_thumb.offsetTop - windowHalfHeight + active_thumb.clientHeight / 2,
            behavior: 'smooth'
          });
        });

        // Stop previous video
        if (previous_media?.classList.contains('product-single__media-external-video')) {
          const previousIframe = previous_media.querySelector('iframe');
          if (previousIframe && previous_media.dataset.provider === 'youtube') {
            previousIframe.contentWindow.postMessage(JSON.stringify({
              event: "command",
              func: "pauseVideo",
              args: ""
            }), "*");
          } else if (previousIframe && previous_media.dataset.provider === 'vimeo') {
            previousIframe.contentWindow.postMessage(JSON.stringify({
              method: "pause"
            }), "*");
          }
          previous_media.querySelector('.product-single__media-external-video--play')?.removeAttribute('hidden');
        } else if (previous_media?.classList.contains('product-single__media-native-video')) {
          previous_media.querySelector('video')?.pause();
        }

        _this.playActiveVideos();

      });

      this.flkty.on('scroll', (progress) => {
        if (!this.progressBar) return;
        const clamped = Math.max(0, Math.min(1, progress));
        this.progressBar.style.width = (clamped * 100) + '%';
      });

      this.updatePagination();

      setTimeout(() => {
        let active_thumbs = Array.from(this.thumbnails).filter(element => element.clientWidth > 0);
        active_thumbs.forEach((thumbnail, index) => {
          thumbnail.addEventListener('click', () => {
            this.thumbnailClick(thumbnail, index);
          });
        });
      });

    }

    bindImagesLoadedFix() {
      if (!this.flkty || this.flkty.__fctImagesLoadedFixBound) return;
      this.flkty.__fctImagesLoadedFixBound = true;
      this.flkty.on('imagesLoaded', () => {
        this.forceViewportHeight();
        this.scheduleLayoutStabilization();
      });
    }

    forceViewportHeight() {
      const viewport = this.querySelector('.flickity-viewport');
      if (!viewport) return;
      if (viewport.offsetHeight > 0) return;

      const firstSlide =
        this.querySelector('.product-images__slide.is-active') ||
        this.querySelector('.product-images__slide');
      if (!firstSlide) return;

      const rect = firstSlide.getBoundingClientRect();
      if (rect.height > 0) {
        viewport.style.height = `${rect.height}px`;
      }

      if (this.flkty) {
        this.flkty.resize();
      }
    }
    onPdpSwapSuccess(event) {
      const swappedContainer = event?.detail?.container;
      if (swappedContainer && !swappedContainer.contains(this)) return;
      this.scheduleLayoutStabilization();
    }
    onLayoutResize(entries) {
      if (!this.flkty || !entries?.length) return;
      const hasVisibleResize = entries.some((entry) => entry.contentRect && entry.contentRect.width > 0 && entry.contentRect.height > 0);
      if (!hasVisibleResize) return;
      this.scheduleLayoutStabilization();
    }
    scheduleLayoutStabilization() {
      if (!this.flkty) return;
      if (this.resizeRaf) {
        cancelAnimationFrame(this.resizeRaf);
      }
      this.resizeRaf = requestAnimationFrame(() => {
        this.resizeRaf = null;
        this.stabilizeLayout();
        requestAnimationFrame(() => this.stabilizeLayout());
      });

      if (this.stabilizeTimeout) {
        clearTimeout(this.stabilizeTimeout);
      }
      this.stabilizeTimeout = setTimeout(() => {
        this.stabilizeTimeout = null;
        this.stabilizeLayout();
      }, 150);
    }
    stabilizeLayout() {
      if (!this.flkty) return;
      this.forceViewportHeight();
      if (typeof this.flkty.reposition === 'function') {
        this.flkty.reposition();
      }
      this.flkty.resize();
      this.updatePagination();
    }
    thumbnailClick(thumbnail, index) {
      [].forEach.call(this.thumbnails, function (el) {
        el.classList.remove('is-initial-selected');
      });
      thumbnail.classList.add('is-initial-selected');
      this.flkty.select(index);
    }
    setDraggable(draggable) {
      this.flkty.options.draggable = draggable;
      this.flkty.updateDraggable();
    }
    selectCell(mediaId) {
      this.flkty.selectCell(mediaId);
    }
    setupProductGallery() {
      if (!this.querySelectorAll('.product-single__media-zoom').length) {
        return;
      }
      this.setEventListeners();
    }
    playActiveVideos() {
      if (!this.flkty) return;

      const activeSlides = this.flkty.selectedElements || [this.flkty.cells[this.flkty.selectedIndex]?.element];
      activeSlides.filter(Boolean).forEach((slide) => {
        const media = slide.querySelector('.product-single__media');
        if (!media) return;

        if (media.classList.contains('product-single__media-native-video')) {
          const video = media.querySelector('video');
          if (!video) return;

          // Muted inline playback is permitted by browsers without a user gesture.
          video.muted = true;
          video.loop = true;
          video.playsInline = true;
          const playPromise = video.play();
          if (playPromise) playPromise.catch(() => {});
        } else if (media.classList.contains('product-single__media-external-video')) {
          this.playExternalVideo(media);
        }
      });
    }
    playExternalVideo(externalVideo) {
      if (externalVideo.dataset.videoLoaded !== 'true') {
        const template = externalVideo.querySelector('.product-single__media-external-video-template');
        if (template) {
          externalVideo.appendChild(template.content.cloneNode(true));
          externalVideo.dataset.videoLoaded = 'true';
        }
      }

      const playOverlay = externalVideo.querySelector('.product-single__media-external-video--play');
      const iframe = externalVideo.querySelector('iframe');
      if (!iframe) return;

      playOverlay?.setAttribute('hidden', '');
      const play = () => {
        if (externalVideo.dataset.provider === 'youtube') {
          iframe.contentWindow?.postMessage(JSON.stringify({
            event: 'command',
            func: 'playVideo',
            args: ''
          }), '*');
        } else if (externalVideo.dataset.provider === 'vimeo') {
          iframe.contentWindow?.postMessage(JSON.stringify({ method: 'play' }), '*');
        }
      };

      iframe.addEventListener('load', play, { once: true });
      play();
    }
    buildItems() {
      this.activeImages = Array.from(this.querySelectorAll('.product-images__slide.is-active .product-single__media-image'));

      return this.activeImages.map((item) => {
        let index = [].indexOf.call(item.parentNode.parentNode.children, item.parentNode);

        let activelink = item.querySelector('.product-single__media-zoom');

        activelink.dataset.index = index;
        return {
          src: activelink.getAttribute('href'),
          msrc: activelink.dataset.msrc,
          w: activelink.dataset.w,
          h: activelink.dataset.h
        };
      });
    }
    setEventListeners() {
      this.links = this.querySelectorAll('.product-single__media-zoom');
      this.pswpElement = document.querySelectorAll('.pswp')[0];
      this.pswpOptions = {
        maxSpreadZoom: 2,
        loop: false,
        allowPanToNext: false,
        closeOnScroll: false,
        showHideOpacity: false,
        arrowKeys: true,
        history: false,
        captionEl: false,
        fullscreenEl: false,
        zoomEl: false,
        shareEl: false,
        counterEl: true,
        arrowEl: true,
        preloaderEl: true,
        timeToIdle: 0,
        timeToIdleOutside: 0,
        tapToToggleControls: false,
        getThumbBoundsFn: () => {
          const thumbnail = this.querySelector('.product-images__slide.is-selected'),
            pageYScroll = window.pageYOffset || document.documentElement.scrollTop,
            rect = thumbnail.getBoundingClientRect();
          return {
            x: rect.left,
            y: rect.top + pageYScroll,
            w: rect.width
          };
        }
      };


      this.links.forEach((link => {
        link.addEventListener('click', (e) => this.zoomClick(e, link));
      }));

      if (this.mobileTwoImage && window.innerWidth <= 1067) {
        this.querySelectorAll('.product-single__media-image').forEach((media) => {
          if (media.dataset.zoomClickBound === 'true') return;
          const zoomLink = media.querySelector('.product-single__media-zoom');
          if (!zoomLink) return;
          media.dataset.zoomClickBound = 'true';
          media.addEventListener('click', (e) => {
            if (e.target.closest('a')) return;
            e.preventDefault();
            zoomLink.click();
          });
        });
      }
    }
    zoomClick(e, link) {
      this.items = this.buildItems();
      this.pswpOptions.index = parseInt(link.dataset.index, 10);
      if (typeof PhotoSwipe !== 'undefined') {
        if (this.pswpElement && this.pswpElement.parentElement !== document.body) {
          document.body.appendChild(this.pswpElement);
        }
        if (this.pswpElement) {
          const viewportWidth = window.visualViewport?.width || window.innerWidth;
          const viewportHeight = window.visualViewport?.height || window.innerHeight;
          this.pswpElement.style.width = `${viewportWidth}px`;
          this.pswpElement.style.height = `${viewportHeight}px`;
          this.pswpElement.style.left = '0';
          this.pswpElement.style.top = '0';
          this.pswpElement.style.right = '0';
          this.pswpElement.style.bottom = '0';
          this.pswpElement.style.transform = 'none';
          const applyUiOffsets = () => {
            const closeButton = this.pswpElement.querySelector('.pswp__button--close');
            const rightArrow = this.pswpElement.querySelector('.pswp__button--arrow--right');
            const viewportWidth = window.visualViewport?.width || window.innerWidth || document.documentElement.clientWidth || 0;

            const setLeftOffset = (el) => {
              if (!el || !viewportWidth) return;
              const width = el.offsetWidth || 0;
              const left = Math.max(0, viewportWidth - width - 15);
              el.style.left = `${left}px`;
              el.style.right = 'auto';
            };

            setLeftOffset(closeButton);
            setLeftOffset(rightArrow);
          };
          requestAnimationFrame(applyUiOffsets);
          window.addEventListener('resize', () => requestAnimationFrame(applyUiOffsets), { passive: true, once: true });
        }
        let pswp = new PhotoSwipe(this.pswpElement, PhotoSwipeUI_Default, this.items, this.pswpOptions);
        pswp.listen('firstUpdate', function () {
          pswp.listen('parseVerticalMargin', function (item) {
            item.vGap = {
              top: 50,
              bottom: 50
            };
          });
        });
        pswp.init();
        pswp.invalidateCurrItems();
        pswp.updateSize(true);
      }
      e.preventDefault();
    }
    bindPaginationButtons() {
      if (!this.pagination) return;
      const prevButton = this.pagination.querySelector('button[name="previous"]');
      const nextButton = this.pagination.querySelector('button[name="next"]');

      if (prevButton) {
        const prev = prevButton.cloneNode(true);
        prevButton.parentNode.replaceChild(prev, prevButton);
        prev.addEventListener('click', (event) => {
          event.preventDefault();
          this.flkty.previous();
        });
        this.paginationPrev = prev;
      }

      if (nextButton) {
        const next = nextButton.cloneNode(true);
        nextButton.parentNode.replaceChild(next, nextButton);
        next.addEventListener('click', (event) => {
          event.preventDefault();
          this.flkty.next();
        });
        this.paginationNext = next;
      }
    }
    updatePagination() {
      if (!this.pagination || !this.flkty) return;
      const current = (this.flkty.selectedIndex || 0) + 1;
      const total = this.flkty.slides ? this.flkty.slides.length : Math.ceil(this.flkty.cells.length / this.getItemsPerPage());
      const currentEl = this.pagination.querySelector('.slider-counter--current');
      const totalEl = this.pagination.querySelector('.slider-counter--total');

      if (currentEl) currentEl.textContent = current;
      if (totalEl) totalEl.textContent = total;

      if (!this.options.wrapAround) {
        if (this.paginationPrev) {
          this.paginationPrev.toggleAttribute('disabled', current <= 1);
        }
        if (this.paginationNext) {
          this.paginationNext.toggleAttribute('disabled', current >= total);
        }
      }

      if (this.progressBar) {
        const progress = total > 1 ? (current - 1) / (total - 1) : 1;
        this.progressBar.style.width = (Math.max(0, Math.min(1, progress)) * 100) + '%';
      }
    }
    getItemsPerPage() {
      if (!this.mobileTwoImage) return 1;
      return window.innerWidth <= 1067 ? 2 : 1;
    }
  }
  customElements.define('product-slider-thumbnails', ProductSliderThumbnails);
}
